import { Dispatch } from 'redux';
import delve from 'dlv';
import kyWithSentry from './helper/kyWithSentry';
import { EntityTypes } from 'context/entityContext';
import { addEntities, shouldLoadEntity } from 'redux/reducers/entity';
import { removeMultipleFollowingEntities } from 'redux/reducers/user/following';
import {
  apiTypeToEntityType,
  entityTypeToApiType,
} from 'helpers/mapEntityTypes';
import {
  IEntityItem,
  IEntity,
  IEntityRaw,
} from 'components/data-containers/Dashboard/types';
import { IIterator } from 'types/shared';

const entityTypes = ['track', 'release', 'artist', 'label', 'project'];

const generateFetches = (types: IIterator, ids: IIterator): IIterator => {
  return types.reduce((fetches: Record<string, unknown>, type: EntityTypes) => {
    if (ids[type]) {
      const fetchItem = kyWithSentry
        .post(`/api/${entityTypeToApiType(type)}/byIds?expand=none`, {
          json: ids[type],
        } as IIterator)
        .json();
      return { ...fetches, [`${type}`]: fetchItem };
    }
    return fetches;
  }, {});
};

const resolveFetches = async (
  allFetches: IIterator,
  dispatch: Dispatch
): Promise<IEntity[]> => {
  const currentEntities: IEntity[] = [];
  for (const type in allFetches) {
    const entities = await allFetches[type];
    const actualEntities = entities.filter(
      (item: IEntity): false | IEntityItem =>
        delve(item, 'code') !== 404 && item.entity
    );
    const removedEntities = entities.filter(
      (item: IEntity): boolean => delve(item, 'code') === 404
    );

    if (actualEntities.length > 0) {
      dispatch(addEntities(apiTypeToEntityType(type), actualEntities));
    }

    if (removedEntities.length > 0) {
      /** Sanitize the entities that are not in Opus anymore. */
      removeMultipleFollowingEntities(
        type,
        removedEntities.map(({ id }: { id: string }) => id)
      );
    }
    currentEntities.push(...actualEntities);
  }
  return currentEntities;
};

const filterEntities = (entities: IEntityRaw): IIterator => {
  const ids: IIterator = {};
  entities.forEach((item: { type: EntityTypes; id: string }): void => {
    /** Check if the followed item isn't already stored in Redux or has an error state */
    if (shouldLoadEntity(item.type, item.id)) {
      if (!ids[item.type]) {
        ids[item.type] = [];
      }

      /** Push the item to a list of entityIds that still need to be loaded */
      ids[item.type].push(item.id);
    }
  });
  return ids;
};

const entitiesInterceptor = async (
  input: RequestInfo /* eslint-disable-line */,
  init: RequestInit | undefined /* eslint-disable-line */,
  interceptionData?: any
): Promise<Response> => {
  try {
    const { entities, dispatch } = interceptionData;
    const idsPerEntityType = filterEntities(entities);
    const fetches = generateFetches(entityTypes, idsPerEntityType);
    const currentEntities = await resolveFetches(fetches, dispatch);

    return new Response(
      JSON.stringify({
        entitiesData: currentEntities,
        entitiesIsNotLoading: entities.length
          ? Object.keys(fetches).length
          : true,
        error: null,
      })
    );
  } catch (error) {
    console.error(
      'An error occured during fetching entities data',
      new Error(error as any)
    );
    return new Response(
      JSON.stringify({
        entitiesData: null,
        entitiesIsLoading: null,
        error: new Error(error as any),
      })
    );
  }
};

export default entitiesInterceptor;
